import axios, { AxiosInstance, CancelTokenSource } from 'axios';
import {
  UploadProgressHandler,
  UploadProtocolError,
  UploadPurpose,
  UploadResult,
  UploadSessionResponse,
  isAllowedUploadExtension,
  isValidUploadSize,
} from './FileUpload.logic';

// Cloud Storage は途中の区切りの長さを 262,144 バイトの倍数にするよう求めている
const UPLOAD_CHUNK_BYTES = 8388608;
const UPLOAD_MAX_STALLED_RESPONSES = 3;
const UPLOAD_REQUEST_TIMEOUT_MILLISECONDS = 120000;
const UPLOAD_RETRY_DELAYS_MILLISECONDS = [1000, 2000, 4000, 8000, 16000];
const CONFIRMED_RANGE_PATTERN = /^bytes=0-([0-9]+)$/;

const FAILURE_MESSAGES: { [kind in UploadFailureKind]: string } = {
  session: '送信の開始に失敗しました',
  http: 'ファイルの送信に失敗しました',
  protocol: '送信の確認結果が不正です',
  retry_exhausted: '通信を再開できませんでした',
  cancelled: 'ファイルの送信を中止しました',
};

interface UploadChunk {
  start: number;
  endExclusive: number;
}

type UploadSessionIssuer = (purpose: UploadPurpose, fileName: string, fileSize: number) => Promise<UploadSessionResponse>;

type UploadFailureKind = 'session' | 'http' | 'protocol' | 'retry_exhausted' | 'cancelled';

// 同じ画面を開いている間だけ保持する送信の途中経過。送信先のアドレスはここ以外に残さない
interface ResumableUploadState {
  purpose: UploadPurpose;
  objectName: string;
  sessionUri: string;
  contentType: string;
  confirmedBytes: number;
  maximumSentBytes: number;
  completed: boolean;
  needsStatusCheck: boolean;
}

interface StorageRequest {
  body: Blob | undefined;
  headers: { [key: string]: string };
  sentUntilBytes: number;
}

type StorageResponse = { succeeded: true; status: number; range: unknown } | { succeeded: false; error: unknown };

// 1回の送信操作の中だけで数える値。再押下のたびに数え直す
interface TransferCounters {
  retriesUsed: number;
  stalledResponses: number;
}

// 答えが無ければまだ何も受け取っていない
function readConfirmedBytes(range: unknown, fileSize: number): number {
  if (range === undefined || range === null) {
    return 0;
  }
  const confirmedBytes = parseConfirmedRange(range);
  if (confirmedBytes !== null && fileSize > 0 && confirmedBytes <= fileSize) {
    return confirmedBytes;
  }
  throw new UploadProtocolError();
}

function parseConfirmedRange(range: unknown): number | null {
  if (typeof range === 'string') {
    const match = CONFIRMED_RANGE_PATTERN.exec(range);
    if (match !== null) {
      return Number(match[1]) + 1;
    }
  }
  return null;
}

function readErrorStatusCode(error: unknown): number | null {
  const response = (error as { response?: { status?: unknown } } | null)?.response;
  if (response !== undefined && typeof response.status === 'number') {
    return response.status;
  }
  return null;
}

export class ResumableUploadClient {
  private readonly issueSession: UploadSessionIssuer;
  private readonly http: AxiosInstance = createStorageHttpClient();
  private readonly cancelSource: CancelTokenSource = axios.CancelToken.source();
  private readonly states = new WeakMap<File, ResumableUploadState>();
  private disposed = false;

  constructor(issueSession: UploadSessionIssuer) {
    this.issueSession = issueSession;
  }

  async upload(file: File, purpose: UploadPurpose, onProgress?: UploadProgressHandler): Promise<UploadResult> {
    this.assertActive(file.name);
    if (!isValidUploadSize(file.size) || !isAllowedUploadExtension(purpose, file.name)) {
      throw new FileUploadError('session', file.name);
    }

    const existing = this.findState(file, purpose);
    if (existing !== undefined && existing.completed) {
      this.notifyProgress(file, existing, onProgress);
      return [existing.objectName, file.size];
    }
    if (existing !== undefined) {
      existing.needsStatusCheck = true;
    }
    const state = existing ?? (await this.startSession(file, purpose));
    this.notifyProgress(file, state, onProgress);
    try {
      return await this.transfer(file, state, onProgress);
    } catch (error) {
      throw this.toUploadError(file, error);
    }
  }

  // 画面を閉じるときに呼ぶ。進行中の通信を止め、以後の送信と進捗の通知を行わない
  dispose(): void {
    this.disposed = true;
    this.cancelSource.cancel();
  }

  private assertActive(fileName: string): void {
    if (this.disposed) {
      throw new FileUploadError('cancelled', fileName);
    }
  }

  // 用途が違う要求は新しい送信として扱う
  private findState(file: File, purpose: UploadPurpose): ResumableUploadState | undefined {
    const state = this.states.get(file);
    if (state !== undefined && state.purpose === purpose) {
      return state;
    }
    return undefined;
  }

  private notifyProgress(file: File, state: ResumableUploadState, onProgress: UploadProgressHandler | undefined): void {
    if (!this.disposed && onProgress !== undefined) {
      onProgress({
        fileName: file.name,
        confirmedBytes: state.confirmedBytes,
        totalBytes: file.size,
      });
    }
  }

  // 送信開始の失敗は自動で再試行しない。応答の形が不正なものは確認結果の不正として扱う
  private async startSession(file: File, purpose: UploadPurpose): Promise<ResumableUploadState> {
    const session = await this.issueSession(purpose, file.name, file.size).catch((error: unknown) => {
      if (error instanceof UploadProtocolError) {
        throw new FileUploadError('protocol', file.name);
      }
      throw new FileUploadError('session', file.name);
    });
    this.assertActive(file.name);
    const state: ResumableUploadState = {
      purpose,
      objectName: session.objectName,
      sessionUri: session.sessionUri,
      contentType: session.contentType,
      confirmedBytes: 0,
      maximumSentBytes: 0,
      completed: false,
      needsStatusCheck: false,
    };
    this.states.set(file, state);
    return state;
  }

  private async transfer(file: File, state: ResumableUploadState, onProgress: UploadProgressHandler | undefined): Promise<UploadResult> {
    const counters: TransferCounters = { retriesUsed: 0, stalledResponses: 0 };
    while (!state.completed) {
      this.assertActive(file.name);
      const request = buildStorageRequest(file, state);
      state.maximumSentBytes = Math.max(state.maximumSentBytes, request.sentUntilBytes);
      const response = await this.send(state.sessionUri, request);
      if (response.succeeded) {
        this.applyResponse(file.size, state, counters, response.status, response.range);
        this.notifyProgress(file, state, onProgress);
      } else {
        await this.recoverFromFailure(file.name, state, counters, response.error);
      }
    }
    return [state.objectName, file.size];
  }

  private async send(sessionUri: string, request: StorageRequest): Promise<StorageResponse> {
    try {
      const response = await this.http.put(sessionUri, request.body, {
        headers: request.headers,
        cancelToken: this.cancelSource.token,
      });
      return { succeeded: true, status: response.status, range: response.headers?.range };
    } catch (error) {
      return { succeeded: false, error };
    }
  }

  private applyResponse(fileSize: number, state: ResumableUploadState, counters: TransferCounters, status: number, range: unknown): void {
    if (isUploadCompletedStatus(status)) {
      state.completed = true;
      state.confirmedBytes = fileSize;
      state.needsStatusCheck = false;
    } else {
      this.applyPartialResponse(fileSize, state, counters, range);
    }
  }

  private applyPartialResponse(fileSize: number, state: ResumableUploadState, counters: TransferCounters, range: unknown): void {
    const nextBytes = readConfirmedBytes(range, fileSize);
    validateConfirmedPosition(state.confirmedBytes, nextBytes, state.maximumSentBytes, fileSize);
    if (nextBytes > state.confirmedBytes) {
      counters.stalledResponses = 0;
    } else {
      counters.stalledResponses += 1;
    }
    if (counters.stalledResponses >= UPLOAD_MAX_STALLED_RESPONSES) {
      throw new UploadProtocolError();
    }
    state.confirmedBytes = nextBytes;
    state.needsStatusCheck = nextBytes === fileSize;
  }

  // 通信断・時間切れ・送信先の一時的な障害は、待ってから受け取り済みの位置を問い合わせ直す
  // 応答が無いものは通信断か時間切れとして扱う
  private async recoverFromFailure(fileName: string, state: ResumableUploadState, counters: TransferCounters, error: unknown): Promise<void> {
    const statusCode = readErrorStatusCode(error);
    const isServerError = statusCode !== null && statusCode >= 500 && statusCode <= 599;
    const isClientError = statusCode !== null && statusCode >= 400 && statusCode <= 499;
    const isRetryableClientError = statusCode === 408 || statusCode === 429;
    const isRetryable = statusCode === null || isServerError || isRetryableClientError;
    if (axios.isCancel(error) || this.disposed) {
      throw new FileUploadError('cancelled', fileName);
    }
    if (!isRetryable && isClientError) {
      throw new FileUploadError('http', fileName);
    }
    if (!isRetryable) {
      throw new UploadProtocolError();
    }
    state.needsStatusCheck = true;
    if (counters.retriesUsed >= UPLOAD_RETRY_DELAYS_MILLISECONDS.length) {
      throw new FileUploadError('retry_exhausted', fileName);
    }
    const delay = UPLOAD_RETRY_DELAYS_MILLISECONDS[counters.retriesUsed];
    // 通信断を挟んだ問い合わせは連続した「進まない応答」に含めない
    counters.retriesUsed += 1;
    counters.stalledResponses = 0;
    await new Promise((resolve) => window.setTimeout(resolve, delay));
    this.assertActive(fileName);
  }

  // 失敗の種類ごとに、途中経過を次の手動実行へ引き継ぐかを決める
  private toUploadError(file: File, error: unknown): FileUploadError {
    const keepsState = error instanceof FileUploadError && error.kind === 'retry_exhausted';
    if (!keepsState) {
      this.states.delete(file);
    }
    if (error instanceof FileUploadError) {
      return error;
    }
    return new FileUploadError('protocol', file.name);
  }
}

export class FileUploadError extends Error {
  readonly kind: UploadFailureKind;
  readonly fileName: string;

  constructor(kind: UploadFailureKind, fileName: string) {
    super(`${FAILURE_MESSAGES[kind]}: ${fileName}`);
    this.name = 'FileUploadError';
    this.kind = kind;
    this.fileName = fileName;
    Object.setPrototypeOf(this, FileUploadError.prototype);
  }
}

// Cloud Storage 専用の通信口。アカウントの認証情報や既定の見出しを一切持たせない
// 308 は途中までしか受け取っていないという Cloud Storage の正常な応答
function createStorageHttpClient(): AxiosInstance {
  const instance = axios.create({
    withCredentials: false,
    timeout: UPLOAD_REQUEST_TIMEOUT_MILLISECONDS,
    validateStatus: (status) => isUploadCompletedStatus(status) || status === 308,
  });
  instance.defaults.headers = {};
  return instance;
}

function isUploadCompletedStatus(status: number): boolean {
  return status === 200 || status === 201;
}

// ファイル全体を読み込まないよう、本体は送る部分だけを切り出す
// 受け取り済みの位置が分からないとき、または全部送り終えたのに完了と言われていないときは問い合わせる
function buildStorageRequest(file: File, state: ResumableUploadState): StorageRequest {
  const chunk = getUploadChunk(file.size, state.confirmedBytes);
  if (!state.needsStatusCheck) {
    if (chunk !== null) {
      return {
        body: file.slice(chunk.start, chunk.endExclusive),
        headers: { 'Content-Type': state.contentType, 'Content-Range': `bytes ${chunk.start}-${chunk.endExclusive - 1}/${file.size}` },
        sentUntilBytes: chunk.endExclusive,
      };
    }
    if (file.size === 0) {
      return {
        body: new Blob([]),
        headers: { 'Content-Type': state.contentType, 'Content-Range': buildStatusContentRange(0) },
        sentUntilBytes: 0,
      };
    }
  }
  return {
    body: undefined,
    headers: { 'Content-Range': buildStatusContentRange(file.size) },
    sentUntilBytes: 0,
  };
}

function getUploadChunk(fileSize: number, start: number): UploadChunk | null {
  const isValidStart = isValidUploadSize(fileSize) && Number.isInteger(start) && start >= 0 && start <= fileSize;
  if (!isValidStart) {
    throw new UploadProtocolError();
  }
  if (start < fileSize) {
    return { start, endExclusive: Math.min(start + UPLOAD_CHUNK_BYTES, fileSize) };
  }
  return null;
}

function buildStatusContentRange(fileSize: number): string {
  if (isValidUploadSize(fileSize)) {
    return `bytes */${fileSize}`;
  }
  throw new UploadProtocolError();
}

// 受け取り済みの位置が戻ったり、まだ送っていない位置まで進んだと答えた場合は信用しない
function validateConfirmedPosition(previousBytes: number, nextBytes: number, maximumSentBytes: number, fileSize: number): void {
  const movedBackward = nextBytes < previousBytes;
  const exceedsSent = nextBytes > maximumSentBytes || nextBytes > fileSize;
  if (movedBackward || exceedsSent) {
    throw new UploadProtocolError();
  }
}
